import { useContext, useState } from 'react';
import { InvoiceService } from '@/services/invoice';
import { Tab, PaymentMethod } from '@/types';
import { PrintSettings, DispatchPrintTab } from '@/types/invoice';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { getTabServerLabel } from '@/services/sales/resolveSaleSeller';

export const useInvoice = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { currentOrganization } = useOrganization();
  const { profile, activeOperator } = useAuth();
  const { toast } = useToast();
  const { translator: t } = useContext(LanguageProviderContext);
  const notifyPrinterDidNotPrint = () => {
    toast({
      title: t('printer.error.savedButNotPrinted'),
      description: t('printer.error.savedButNotPrintedDescription'),
      variant: 'destructive',
    });
  };

  const generateTabInvoice = async (
    tab: Tab,
    options: {
      settings?: Partial<PrintSettings>;
      forPreview?: boolean;
    },
    paymentMethod?: PaymentMethod,
    cashPaymentDetails?: { amountReceived: number; changeGiven: number },
  ) => {
    if (!currentOrganization) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const serverName = getTabServerLabel(tab, { profile, activeOperator });
      const tabForInvoice = serverName ? { ...tab, serverName } : tab;

      const invoiceData = await InvoiceService.createInvoiceFromTab(
        tabForInvoice,
        currentOrganization,
        paymentMethod,
        options.forPreview,
        cashPaymentDetails
      );

      const orgLanguage = currentOrganization.settings?.invoiceLanguage || 'es';
      const orgCurrency = currentOrganization.currency || 'USD';
      const settingsWithLanguageAndCurrency = {
        ...options.settings,
        language: orgLanguage,
        currency: orgCurrency
      };

      await InvoiceService.printInvoice(invoiceData, settingsWithLanguageAndCurrency);
    } catch (error) {
      console.error('Error generating tab invoice:', error);
      notifyPrinterDidNotPrint();
    } finally {
      setIsGenerating(false);
    }
  };

  const generateDispatchOrder = async (tab: Tab | DispatchPrintTab) => {
    setIsGenerating(true);
    try {
      await InvoiceService.printDispatch(tab, currentOrganization, { variant: 'order' });
    } catch (error) {
      console.error('Error generating dispatch order:', error);
      notifyPrinterDidNotPrint();
    } finally {
      setIsGenerating(false);
    }
  };

  const generateDispatchCancellation = async (tab: Tab | DispatchPrintTab) => {

    try {
      await InvoiceService.printDispatch(tab, currentOrganization, { variant: 'cancellation' });
    } catch (error) {
      console.error('Error generating dispatch cancellation:', error);
      notifyPrinterDidNotPrint();
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateTabInvoice,
    generateDispatchOrder,
    generateDispatchCancellation,
    isGenerating,
  };
};
